import React from 'react';

import Badge from 'components/partials/badge/badge';
import Button from 'components/partials/button/button';
import IconButton from 'components/partials/button/icon-button';
import ActionsDropdown from 'components/partials/actions-dropdown';
import type { ActionsDropdownOption } from 'components/partials/actions-dropdown/actions-dropdown';

import InlineEditableTitle from './inline-editable-title';

import { formatDate } from 'helpers/dates-formatter';

import { DATASET_DATE_FORMAT } from '../../aid-studio-details.constants';

import Icons from 'assets/icons';

interface ModelHeaderProps {
  name: string;
  canEdit: boolean;
  canArchive?: boolean;
  isArchived?: boolean;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  datasetCreatedDate?: string;
  lastRecreatedDate?: string;
  onRename: (name: string) => Promise<void>;
  onSave: () => void;
  onRevertChanges: () => void;
  onSaveAsNewVersion: () => void;
  onCopyToEdit: () => void;
  onArchive?: () => void;
  onRestore?: () => void;
  onQuickSummary?: () => void;
  showQuickSummary: boolean;
}

const ModelHeader = ({
  name,
  canEdit,
  canArchive = false,
  isArchived = false,
  hasUnsavedChanges,
  isSaving,
  datasetCreatedDate,
  lastRecreatedDate,
  onRename,
  onSave,
  onRevertChanges,
  onSaveAsNewVersion,
  onCopyToEdit,
  onArchive,
  onRestore,
  onQuickSummary,
  showQuickSummary,
}: ModelHeaderProps): React.JSX.Element => {
  const showUnsavedBadge = canEdit && hasUnsavedChanges;

  const datasetLine = lastRecreatedDate
    ? `Last recreated: ${formatDate(lastRecreatedDate, DATASET_DATE_FORMAT)}`
    : datasetCreatedDate &&
      `Dataset created: ${formatDate(datasetCreatedDate, DATASET_DATE_FORMAT)}`;

  const menuOptions: ActionsDropdownOption[] = [];

  if (canEdit) {
    menuOptions.push({
      label: 'Clone',
      startIcon: <Icons.Copy className="size-4" aria-hidden="true" />,
      onClick: onSaveAsNewVersion,
    });
  }

  if (canArchive) {
    menuOptions.push(
      isArchived
        ? {
            label: 'Restore',
            startIcon: <Icons.Release className="size-4" aria-hidden="true" />,
            onClick: onRestore,
          }
        : {
            label: 'Archive',
            startIcon: <Icons.Archived className="size-4" aria-hidden="true" />,
            onClick: onArchive,
          }
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-start gap-5">
        <div className="flex flex-col gap-1 min-w-0">
          <InlineEditableTitle
            name={name}
            canEdit={canEdit}
            isSaving={isSaving}
            onRename={onRename}
          />

          <div className="flex items-center gap-3 h-6">
            {showUnsavedBadge ? (
              <Badge
                text="Changes not saved"
                variant="neutral-filled"
                data-testid="changes-not-saved-badge"
              />
            ) : (
              <>
                {datasetLine && (
                  <span
                    className="text-xs text-gray-700"
                    data-testid="dataset-metadata-line"
                  >
                    {datasetLine}
                  </span>
                )}
                {!canEdit && (
                  <Badge
                    text="Read-only mode"
                    variant="neutral-filled"
                    data-testid="read-only-badge"
                  />
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex gap-3 items-center ml-auto shrink-0">
          {canEdit ? (
            <>
              <Button
                type="button"
                variant="brand-outlined"
                size="sm"
                onClick={onRevertChanges}
                disabled={!hasUnsavedChanges}
                data-testid="revert-changes-button"
              >
                Revert changes
              </Button>
              <Button
                type="button"
                variant="brand-filled"
                size="sm"
                onClick={onSave}
                disabled={!hasUnsavedChanges || isSaving}
                loading={isSaving}
                data-testid="save-model-button"
              >
                Save
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="reserve-outlined"
              size="sm"
              iconBefore={<Icons.Copy className="size-4 shrink-0" />}
              onClick={onCopyToEdit}
              data-testid="copy-to-edit-button"
            >
              Clone to edit
            </Button>
          )}

          {showQuickSummary && (
            <IconButton
              size="sm"
              variant="brand-outlined"
              icon={<Icons.Checklist />}
              onClick={onQuickSummary}
              className="shadow-sm"
              aria-label="Quick summary"
              data-testid="quick-summary-button"
            />
          )}

          {!!menuOptions.length && (
            <ActionsDropdown
              options={menuOptions}
              ariaLabel="Model actions"
              customClass="size-8 p-0 border-solid"
              menuCustomClass="w-[190px] [&_button]:font-normal"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ModelHeader;
